import {
    ApiInboxMarkReadReqSchema,
    ApiInboxMarkReadResSchema,
    ApiInboxMessagesResSchema,
    ApiInboxOpenReqSchema,
    ApiInboxOpenResSchema,
    ApiInboxClaimReqSchema,
    ApiInboxClaimResSchema,
    ApiInboxReadV2ReqSchema,
    ApiInboxReadV2ResSchema,
    type ApiInboxMessageV2,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";
import { useRequestSlotMachineRefetch } from "./useSlotMachine";
import { useInboxSummary } from "./useInboxSummary";
import i18n from "../i18n/i18n";

class InboxApiError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "InboxApiError";
    }
}

const messagingEnabled = import.meta.env.VITE_PB_MESSAGING_ENABLED === "true";

export function useInbox(enabled: boolean, actionToken?: string) {
    const { notificationCount, refreshSummary } = useInboxSummary(
        enabled && messagingEnabled,
    );
    const requestSlotRefresh = useRequestSlotMachineRefetch();
    const { setSlice: setWheel } = useSlice("wheelSlice");
    const { setSlice: setGnome } = useSlice("gnomeCustomizationSlice");
    const requestRewardRefresh = useCallback(() => {
        requestSlotRefresh();
        setWheel((current) => ({
            refreshRequestedRevision: current.refreshRequestedRevision + 1,
        }));
        setGnome((current) => ({
            refreshRequestedRevision: current.refreshRequestedRevision + 1,
        }));
    }, [requestSlotRefresh, setWheel, setGnome]);
    const actionRequest = useRef<{
        token: string;
        requestId: string;
        completed: boolean;
    } | null>(null);
    const claimRequests = useRef(new Map<string, string>());
    const [messages, setMessages] = useState<ApiInboxMessageV2[]>([]);
    const [status, setStatus] = useState<
        "idle" | "loading" | "ready" | "error"
    >("idle");
    const [error, setError] = useState<string | null>(null);
    const [unreadCount, setUnreadCount] = useState("0");
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const requestRef = useRef<AbortController | null>(null);
    const markReadChainRef = useRef<Promise<void>>(Promise.resolve());

    const load = useCallback(
        async (cursor?: string) => {
            if (!enabled) return;
            requestRef.current?.abort();
            const controller = new AbortController();
            requestRef.current = controller;
            setStatus("loading");
            setError(null);
            try {
                if (
                    actionToken &&
                    actionRequest.current?.token !== actionToken
                ) {
                    actionRequest.current = {
                        token: actionToken,
                        requestId: crypto.randomUUID(),
                        completed: false,
                    };
                }
                const pending = actionRequest.current;
                const query = new URLSearchParams({
                    limit: "50",
                    ...(cursor ? { cursor } : {}),
                });
                const { body } = messagingEnabled
                    ? await authenticatedApiCall(
                          "/api/v2/inbox/open",
                          ApiInboxOpenResSchema,
                          {
                              method: "POST",
                              signal: controller.signal,
                              body: JSON.stringify(
                                  ApiInboxOpenReqSchema.parse({
                                      requestId:
                                          pending?.requestId ??
                                          crypto.randomUUID(),
                                      cursor,
                                      limit: 50,
                                      actionToken:
                                          !cursor &&
                                          pending &&
                                          !pending.completed
                                              ? pending.token
                                              : undefined,
                                  }),
                              ),
                          },
                      )
                    : await authenticatedApiCall(
                          `/api/v1/inbox?${query}`,
                          ApiInboxMessagesResSchema,
                          { signal: controller.signal },
                      );
                if (body.status === "ERROR") {
                    throw new InboxApiError(
                        i18n.t("inbox.errors.load", { code: body.code }),
                    );
                }
                if (requestRef.current !== controller) return;
                const result =
                    "actionMessage" in body ? body.actionMessage : null;
                const actionError =
                    "actionError" in body ? body.actionError : null;
                if ((result || actionError) && pending)
                    pending.completed = true;
                if (actionError) setError(i18n.t("inbox.errors.action"));
                const page = result
                    ? [
                          result,
                          ...body.messages.filter((m) => m.id !== result.id),
                      ]
                    : body.messages;
                setMessages((current) =>
                    cursor
                        ? [
                              ...current,
                              ...page.filter(
                                  (m) =>
                                      !current.some((old) => old.id === m.id),
                              ),
                          ]
                        : page,
                );
                if (result?.rewardState === "CLAIMED") requestRewardRefresh();
                setUnreadCount(body.unreadCount);
                setNextCursor(body.nextCursor);
                setStatus("ready");
                void refreshSummary();
            } catch (loadError) {
                if (controller.signal.aborted) return;
                setStatus("error");
                setError(
                    loadError instanceof InboxApiError
                        ? loadError.message
                        : i18n.t("inbox.errors.load", { code: "UNKNOWN" }),
                );
            } finally {
                if (requestRef.current === controller)
                    requestRef.current = null;
            }
        },
        [enabled, actionToken, requestRewardRefresh, refreshSummary],
    );

    useEffect(() => {
        if (enabled) void load();
        return () => requestRef.current?.abort();
    }, [enabled, load]);

    useEffect(() => {
        if (!enabled) return;

        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") void load();
        };

        document.addEventListener("visibilitychange", refreshWhenVisible);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                refreshWhenVisible,
            );
    }, [enabled, load]);

    const markRead = useCallback(
        (messageIds: readonly string[]) => {
            const uniqueMessageIds = [...new Set(messageIds)];
            if (uniqueMessageIds.length === 0) return Promise.resolve();

            const run = async () => {
                const request = (
                    messagingEnabled
                        ? ApiInboxReadV2ReqSchema
                        : ApiInboxMarkReadReqSchema
                ).parse({
                    messageIds: uniqueMessageIds,
                });
                const { body } = await authenticatedApiCall(
                    messagingEnabled ? "/api/v2/inbox/read" : "/api/v1/inbox",
                    messagingEnabled
                        ? ApiInboxReadV2ResSchema
                        : ApiInboxMarkReadResSchema,
                    {
                        method: messagingEnabled ? "POST" : "PATCH",
                        body: JSON.stringify(request),
                    },
                ).catch(() => {
                    throw new Error(
                        i18n.t("inbox.errors.markRead", { code: "UNKNOWN" }),
                    );
                });
                if (body.status === "ERROR") {
                    throw new Error(
                        i18n.t("inbox.errors.markRead", { code: body.code }),
                    );
                }
                setUnreadCount(body.unreadCount);
                setMessages((current) =>
                    current.map((message) =>
                        body.messageIds.includes(message.id)
                            ? { ...message, readAt: body.readAt }
                            : message,
                    ),
                );
                void refreshSummary();
            };

            const queuedRequest = markReadChainRef.current.then(run, run);
            markReadChainRef.current = queuedRequest.catch(() => undefined);
            return queuedRequest;
        },
        [refreshSummary],
    );

    const claim = useCallback(
        async (messageId: string) => {
            const requestId =
                claimRequests.current.get(messageId) ?? crypto.randomUUID();
            claimRequests.current.set(messageId, requestId);
            const { body } = await authenticatedApiCall(
                "/api/v2/inbox/claim",
                ApiInboxClaimResSchema,
                {
                    method: "POST",
                    body: JSON.stringify(
                        ApiInboxClaimReqSchema.parse({ requestId, messageId }),
                    ),
                },
            );
            if (body.status === "ERROR")
                throw new Error(
                    i18n.t("inbox.errors.claim", { code: body.code }),
                );
            setMessages((current) =>
                current.map((message) =>
                    message.id === body.message.id ? body.message : message,
                ),
            );
            requestRewardRefresh();
            void refreshSummary();
            return body.message;
        },
        [requestRewardRefresh, refreshSummary],
    );

    const refresh = useCallback(() => load(), [load]);
    const loadMore = useCallback(
        () => (nextCursor ? load(nextCursor) : Promise.resolve()),
        [load, nextCursor],
    );

    return {
        messages,
        status,
        error,
        unreadCount,
        notificationCount: messagingEnabled
            ? (notificationCount ?? unreadCount)
            : unreadCount,
        nextCursor,
        refresh,
        loadMore,
        markRead,
        claim,
    };
}

export type InboxController = ReturnType<typeof useInbox>;
